import React, { useState } from 'react';
import { useSettingsReadOnly } from './readOnlyContext';

const SettingCard = ({ name, value, unit, min, max, onEdit, readOnly: readOnlyProp = false, showButton = true, isMode = false, isFooter = false, type, options }) => {
  const ctxReadOnly = useSettingsReadOnly();
  const readOnly = readOnlyProp || ctxReadOnly;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onEdit(name, editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const startEditing = () => {
    setEditValue(value);
    setIsEditing(true);
  };
  
  // Format the display name - use the provided name as-is
  const displayName = name;
  
  // Format value display - show all values without truncating
  const formatValue = (val) => {
    if (isMode) {
      // Handle mode display
      return val === 0 ? 'Local mode' : val === 1 ? 'Manual' : 'Auto';
    }
    if (val === null || val === undefined) return '--';
    
    // If value is already a string (pre-formatted), return as is
    if (typeof val === 'string') {
      return val;
    }
    
    const numVal = parseFloat(val);
    
    // For zero, just return 0
    if (numVal === 0) {
      return '0';
    }
    
    // For very small numbers, show more precision
    if (Math.abs(numVal) < 0.000001) {
      return numVal.toFixed(10);
    } else if (Math.abs(numVal) < 0.001) {
      return numVal.toFixed(8);
    } else if (Math.abs(numVal) < 1) {
      return numVal.toFixed(6);
    } else if (numVal % 1 === 0) {
      // For whole numbers, show without decimals
      return numVal.toFixed(0);
    } else {
      // For other numbers, show with 2 decimal places
      return numVal.toFixed(2);
    }
  };
  
  // On/off setting: no edit mode, the two buttons write directly
  if (type === 'boolean') {
    const boolVal = value === true || value === 'true' || value === 1;
    return (
      <div className="bg-white rounded-lg border border-gray-300 shadow-sm">
        <div className="p-4 pb-2">
          <p className="text-gray-700 font-medium text-center mb-3 border-b pb-2 capitalize">{displayName}</p>
          <p className={`text-3xl font-bold text-center mb-3 ${boolVal ? 'text-green-600' : 'text-red-600'}`}>
            {boolVal ? 'Enabled' : 'Disabled'}
          </p>
        </div>
        {!readOnly && (
          <div className="flex">
            <button
              onClick={() => !boolVal && onEdit(name, true)}
              className={`flex-1 px-4 py-3 font-semibold border-r border-gray-300 rounded-bl-lg transition-colors ${boolVal ? 'bg-[#2a4e7c] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Enabled
            </button>
            <button
              onClick={() => boolVal && onEdit(name, false)}
              className={`flex-1 px-4 py-3 font-semibold rounded-br-lg transition-colors ${!boolVal ? 'bg-[#2a4e7c] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Disabled
            </button>
          </div>
        )}
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="bg-white rounded-lg border-2 border-blue-400 shadow-lg p-4">
        <p className="text-gray-700 font-medium text-center mb-3 border-b pb-2 capitalize">{displayName}</p>
        <div className="flex items-center gap-2 justify-center">
          {type === 'select' ? (
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-40 px-3 py-2 text-xl font-bold text-center border-2 border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            >
              {options?.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <input
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              min={min}
              max={max}
              step="any"
              className="w-40 px-3 py-2 text-xl font-bold text-center border-2 border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          )}
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-colors"
          >
            Save
          </button>
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded hover:bg-red-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }
  
  // Special handling for footer display
  if (isFooter) {
    return (
      <div className="bg-white rounded-lg border-2 border-blue-500 p-4 text-center">
        <p className="text-3xl font-bold text-gray-900 mb-2">
          {formatValue(value)}
        </p>
        <p className="text-gray-600 font-semibold capitalize">{displayName}</p>
      </div>
    );
  }
  
  // Special handling for mode selector
  if (isMode) {
    return (
      <div className="bg-white rounded-lg border border-gray-300 shadow-sm">
        <div className="p-4 pb-2">
          <p className="text-gray-700 font-medium text-center mb-3 border-b pb-2 capitalize">{displayName}</p>
          <p className="text-2xl font-bold text-center text-gray-900 mb-3">
            {formatValue(value)}
          </p>
        </div>
        <div className="flex">
          <button className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 font-semibold border-r border-gray-300">
            Local
          </button>
          <button className="flex-1 px-4 py-3 bg-[#2a4e7c] text-white font-semibold">
            Manual
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`bg-white rounded-lg border border-gray-300 shadow-sm ${!showButton ? 'p-4' : ''}`}>
      {showButton ? (
        <>
          <div className="p-4 pb-2">
            <p className="text-gray-700 font-medium text-center mb-3 border-b pb-2 capitalize">{displayName}</p>
            <p className="text-3xl font-bold text-center text-gray-900 mb-3">
              {type === 'select'
                ? (options?.find((opt) => opt.value === value)?.label ?? '--')
                : formatValue(value)}
            </p>
          </div>
          {!readOnly && (
            <button
              onClick={startEditing}
              className="w-full px-4 py-3 bg-[#2a4e7c] text-white font-semibold hover:bg-[#1e3a5f] transition-colors"
            >
              Change
            </button>
          )}
        </>
      ) : (
        <div className="text-center">
          <p className="text-3xl font-bold text-gray-900 mb-2">{formatValue(value)}</p>
          <p className="text-gray-600 font-medium capitalize">{displayName}</p>
        </div>
      )}
    </div>
  );
};

export default SettingCard;